const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const TOKEN_KEY = 'plevits_id_token';
const ACCESS_TOKEN_KEY = 'plevits_access_token';
const REFRESH_TOKEN_KEY = 'plevits_refresh_token';

class ApiError extends Error {
  public code?: string;
  public response?: Record<string, unknown>;

  constructor(
    public status: number,
    public statusText: string,
    message?: string,
    code?: string,
    response?: Record<string, unknown>
  ) {
    super(message || statusText);
    this.name = 'ApiError';
    this.code = code;
    this.response = response;
  }
}

async function refreshTokens(): Promise<string | null> {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) return null;

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) return null;

    const data = await response.json();
    localStorage.setItem(TOKEN_KEY, data.idToken);
    localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
    return data.idToken;
  } catch {
    return null;
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  // Auth routes need the access token (for Cognito GetUser API)
  // All other routes use the ID token (validated by Cognito authorizer on API Gateway)
  const isAuthRoute = endpoint.startsWith('/auth/');
  const token = isAuthRoute
    ? localStorage.getItem(ACCESS_TOKEN_KEY)
    : localStorage.getItem(TOKEN_KEY);

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  let response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  // Handle token refresh on 401 (only for non-auth endpoints)
  if (response.status === 401 && token && !isAuthRoute) {
    const newToken = await refreshTokens();
    if (newToken) {
      response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers: {
          ...headers,
          Authorization: `Bearer ${newToken}`,
        },
      });
    }
    // If refresh failed, just let it fall through to the error below
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      response.status,
      response.statusText,
      errorData.message || errorData.error,
      errorData.code,
      errorData
    );
  }

  // Handle empty responses
  const text = await response.text();
  if (!text) return {} as T;

  return JSON.parse(text);
}

export const api = {
  get<T>(endpoint: string): Promise<T> {
    return request<T>(endpoint, { method: 'GET' });
  },

  post<T>(endpoint: string, data?: unknown): Promise<T> {
    return request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  patch<T>(endpoint: string, data: unknown): Promise<T> {
    return request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  delete<T>(endpoint: string): Promise<T> {
    return request<T>(endpoint, { method: 'DELETE' });
  },
};

export const billing = {
  createCheckoutSession(billingPeriod: 'monthly' | 'annual', currency: string) {
    return request<{ sessionId: string; url: string }>('/billing/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify({ billingPeriod, currency }),
    });
  },

  createPortalSession() {
    return request<{ url: string }>('/billing/create-portal-session', {
      method: 'POST',
    });
  },

  getStatus() {
    return request<{
      /** Must match Plan type in packages/types/src/index.ts */
      plan: 'free' | 'pro' | 'enterprise';
      stripeCustomerId?: string;
      stripeSubscriptionId?: string;
      subscriptionStatus?: string;
      currentPeriodEnd?: string;
      cancelAtPeriodEnd?: boolean;
      preferredCurrency?: 'GBP' | 'USD' | 'EUR';
    }>('/billing/status', { method: 'GET' });
  },

  verifySession(sessionId: string) {
    return request<{
      verified: boolean;
      plan?: 'pro';
      subscriptionStatus?: string;
      reason?: string;
      paymentStatus?: string;
      amount?: number;
      currency?: string;
    }>('/billing/verify-session', {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    });
  },

  getSubscriptionDetails() {
    return request<import('@plevits/types').SubscriptionDetails>('/billing/subscription-details');
  },

  cancelSubscription() {
    return request<{ status: string; message: string }>('/billing/cancel-subscription', {
      method: 'POST',
    });
  },

  reactivateSubscription() {
    return request<{ status: string; message: string }>('/billing/reactivate-subscription', {
      method: 'POST',
    });
  },

  updateBillingPeriod(billingPeriod: 'monthly' | 'annual') {
    return request<{ status: string }>('/billing/update-billing-period', {
      method: 'POST',
      body: JSON.stringify({ billingPeriod }),
    });
  },

  getProrationPreview(billingPeriod: 'monthly' | 'annual') {
    return request<import('@plevits/types').ProrationPreview>(
      `/billing/proration-preview?billingPeriod=${billingPeriod}`
    );
  },

  getInvoices(cursor?: string) {
    const params = cursor ? `?cursor=${cursor}` : '';
    return request<import('@plevits/types').InvoicesResponse>(`/billing/invoices${params}`);
  },

  createSetupIntent() {
    return request<import('@plevits/types').SetupIntentResponse>('/billing/create-setup-intent', {
      method: 'POST',
    });
  },
};

export const contact = {
  /** Must match ContactSalesRequest in packages/types/src/index.ts */
  submitSalesForm(data: {
    name: string;
    email: string;
    company: string;
    teamSize: string;
    message: string;
    source?: string;
    website?: string;
  }) {
    /** Must match ContactSalesResponse in packages/types/src/index.ts */
    return request<{ success: boolean; message: string }>('/contact-sales', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

export const preferences = {
  updateEmailPreferences(data: { monthlyReportsEnabled: boolean }) {
    return request<{ message: string }>('/auth/me', {
      method: 'PATCH',
      body: JSON.stringify({ emailPreferences: data }),
    });
  },
  updateCurrencyPreference(preferredCurrency: import('@plevits/types').SupportedCurrency) {
    return request<{ message: string }>('/auth/me', {
      method: 'PATCH',
      body: JSON.stringify({ preferredCurrency }),
    });
  },
};

export const security = {
  changePassword(currentPassword: string, newPassword: string) {
    return request<{ message: string }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },

  setupMfa() {
    return request<{ secretCode: string; otpauthUri: string }>('/auth/mfa/setup', {
      method: 'POST',
    });
  },

  verifyMfa(code: string) {
    return request<{ mfaEnabled: boolean }>('/auth/mfa/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  disableMfa(password: string, mfaCode?: string) {
    return request<{ mfaEnabled: boolean }>('/auth/mfa/disable', {
      method: 'POST',
      body: JSON.stringify({ password, ...(mfaCode && { mfaCode }) }),
    });
  },

  deleteAccount(password: string, mfaCode?: string) {
    return request<{ message: string }>('/auth/account/delete', {
      method: 'POST',
      body: JSON.stringify({ password, ...(mfaCode && { mfaCode }) }),
    });
  },
  mfaChallenge(session: string, code: string, email: string) {
    return request<{
      accessToken: string;
      refreshToken: string;
      idToken: string;
      expiresIn: number;
    }>('/auth/mfa/challenge', {
      method: 'POST',
      body: JSON.stringify({ session, code, email }),
    });
  },
};

export { ApiError };
